import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { assetUrl } from "@/lib/site";
import { CategoryIcon } from "@/lib/categoryIcons";

export const useMenu = () => {
  const q = useQuery({ queryKey: ["public-menu"], queryFn: () => api.get("/public/menu").then((r) => r.data), staleTime: 10 * 60 * 1000 });
  return { categories: [], stores: [], ...q.data, loading: q.isLoading };
};

const MENUS = {
  "/categories": { key: "categories", title: "Top categories", all: "All categories", to: (x) => `/category/${x.slug}` },
  "/stores": { key: "stores", title: "Top stores", all: "All stores", to: (x) => `/store/${x.slug}` },
};
export const hasMenu = (path) => !!MENUS[path];

const Thumb = ({ item }) => item.logo_url
  ? <img src={assetUrl(item.logo_url)} alt="" loading="lazy" className="h-7 w-7 rounded-md object-contain bg-white border border-slate-100 shrink-0" />
  : <CategoryIcon icon={item.icon} name={item.name} />;

const Items = ({ path, onPick, testid }) => {
  const m = MENUS[path];
  const menu = useMenu();
  const items = menu[m.key];
  if (menu.loading) return <p className="text-sm text-slate-400 px-2 py-2 animate-pulse" data-testid={`${testid}-loading`}>Loading…</p>;
  return (
    <>
      {items.length > 0 ? (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {items.map((x) => (
            <li key={x.id}>
              <Link to={m.to(x)} onClick={onPick} data-testid={`${testid}-item-${x.slug}`}
                className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50 transition-colors">
                <Thumb item={x} />
                <span className="font-semibold text-slate-800 truncate">{x.name}</span>
                {x.count > 0 && <span className="ml-auto text-xs text-slate-400 shrink-0">{x.count}</span>}
              </Link>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-slate-500 px-2 py-2">Nothing live here yet.</p>}
      <Link to={path} onClick={onPick} data-testid={`${testid}-view-all`} className="block mt-2 px-2 py-2 text-sm font-bold text-emerald-700 hover:underline">{m.all} →</Link>
    </>
  );
};

// Desktop/tablet dropdown: opens on hover or keyboard focus; the main link still opens the full page.
export const MegaMenu = ({ path, children }) => {
  const [open, setOpen] = useState(false);
  const openedAt = useRef(0);
  const show = () => { if (!open) openedAt.current = Date.now(); setOpen(true); };
  const loc = useLocation().pathname;
  useEffect(() => setOpen(false), [loc]);
  return (
    <div onMouseEnter={show} onMouseLeave={() => setOpen(false)}
      onFocus={show} onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) && setOpen(false)}
      onKeyDown={(e) => e.key === "Escape" && setOpen(false)}>
      <div className="flex items-center">
        {children}
        <button type="button" aria-label={`Show ${MENUS[path].title.toLowerCase()}`} aria-expanded={open}
          onClick={() => (open && Date.now() - openedAt.current > 400 ? setOpen(false) : show())}
          className="-ml-2 p-1 rounded-full text-slate-500 hover:text-slate-900" data-testid={`megamenu-toggle-${MENUS[path].key}`}>
          <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="absolute left-4 sm:left-6 lg:left-8 top-full pt-2 z-50" data-testid={`megamenu-${MENUS[path].key}`}>
          <div className="w-[min(560px,calc(100vw-3rem))] bg-white rounded-2xl border border-slate-200 shadow-xl p-4 animate-in fade-in slide-in-from-top-1">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 px-2 mb-2">{MENUS[path].title}</p>
            <Items path={path} onPick={() => setOpen(false)} testid={`megamenu-${MENUS[path].key}`} />
          </div>
        </div>
      )}
    </div>
  );
};

// Phone drawer: tap-to-open section under the main link.
export const MobileSection = ({ path, onPick }) => {
  const [open, setOpen] = useState(false);
  const m = MENUS[path];
  return (
    <div className="border-b border-slate-100">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`mobile-section-${m.key}`}
        className="w-full flex items-center justify-between py-2 text-sm font-semibold text-slate-500">
        {m.title} <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-2 text-sm"><Items path={path} onPick={onPick} testid={`mobile-section-${m.key}`} /></div>}
    </div>
  );
};

// Phones: hide the logo row while scrolling down, bring it back on any scroll up.
export const useHideOnScroll = () => {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - last) < 8) return;
      setHidden(window.innerWidth < 768 && y > last && y > 80);
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return hidden;
};
